import React from "react";
import {
  Box,
  ProgressPie,
  resolveEntityColor,
  toColor,
  type ProgressPieOverlay,
} from "@salestrak/ui";
import type { Omittable } from "@salestrak/ui";

const SIZE_MAP: Record<string, number> = {
  "2xs": 12,
  xs: 14,
  sm: 16,
  md: 18,
  lg: 21,
  xl: 24,
};

export type StageIconProps = Omittable<{
  // The nested object relaxes too — `Omittable` is not recursive, and callers
  // hand this the same stage they built inline from nullable fragment fields.
  stage?: Omittable<{
    type?: string | null;
    icon?: Omittable<{ color?: string | null; percentage?: number | null }> | null;
  }> | null;
  size?: "2xs" | "xs" | "sm" | "md" | "lg" | "xl" | number;
  padded?: boolean;
  /** Paint: the stage's own hue (default), or `current` — the surrounding
   *  text color, for a pie riding a tinted chip whose ink is already the
   *  hue's legible rung. */
  color?: "entity" | "current";
}>;

export const StageIcon = React.memo(function StageIcon({
  stage,
  size: sizeProp = "sm",
  padded,
  color = "entity",
}: StageIconProps) {
  const px = typeof sizeProp === "number" ? sizeProp : (SIZE_MAP[sizeProp] ?? 16);
  const stageType = stage?.type;
  // An SVG `fill` resolves var() and re-resolves on a theme flip, so the
  // token reference goes straight through — there is nothing to compute.
  const resolvedColor =
    color === "current"
      ? "currentColor"
      : resolveEntityColor(toColor(stage?.icon?.color));
  const resolvedPct =
    stageType === "done" || stageType === "canceled"
      ? 100
      : (stage?.icon?.percentage ?? 0);

  const overlay: ProgressPieOverlay | undefined =
    stageType === "done" ? "check" :
    stageType === "canceled" ? "x" :
    undefined;

  const icon = (
    <ProgressPie
      percentage={resolvedPct}
      color={resolvedColor}
      size={px}
      overlay={overlay}
      emptyRing="dashed"
    />
  );

  if (padded) {
    return (
      <Box
        direction="row"
        align="center"
        justify="center"
        rounded="sm"
        padding="xs"
      >
        {icon}
      </Box>
    );
  }

  return icon;
});
